/**
 * coupon_lines para POST /orders y PUT /list/:id (formato WooCommerce-like).
 * Descuento = suma de line_items[].discount (mismo valor que "Descuento" en totales del pedido).
 */

import type { ExtraDetails, LineItem } from '../types/orderFormTypes'

export const ORDER_COUPON_DISCOUNT_TYPE_FIXED_CART = 'fixed_cart' as const

export type OrderCouponDiscountType = typeof ORDER_COUPON_DISCOUNT_TYPE_FIXED_CART

export interface OrderCouponLine {
  discount: number
  discount_type: OrderCouponDiscountType
  nominal_amount: number
}

function parseCouponAmount(raw: unknown): number {
  if (raw === undefined || raw === null || raw === '') return 0
  const n = Number(String(raw).replace(/[^\d.-]/g, ''))
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.round(n))
}

/** Suma descuentos por línea (igual que calculateTotals → totals.discount en la UI). */
function sumLineItemsDiscount(lineItems: LineItem[] | undefined): number {
  if (!lineItems?.length) return 0
  const total = lineItems.reduce((sum, item) => {
    const d = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0
    return sum + (Number.isFinite(d) ? d : 0)
  }, 0)
  return Math.max(0, Math.round(total))
}

/**
 * Construye coupon_lines para el JSON del pedido (create/update).
 * Vacío si el descuento total de líneas es 0.
 */
export function buildOrderCouponLines(formData: {
  lineItems?: LineItem[]
}): OrderCouponLine[] {
  const amount = sumLineItemsDiscount(formData.lineItems)
  if (amount <= 0) {
    return []
  }
  return [
    {
      discount: amount,
      discount_type: ORDER_COUPON_DISCOUNT_TYPE_FIXED_CART,
      nominal_amount: amount
    }
  ]
}

/** Al cargar pedido: si el API envía coupon_lines, reflejar en extraDetails (retención). */
export function mergeRetencionFromCouponLines(initialData: {
  coupon_lines?: unknown
  extraDetails?: ExtraDetails
}): { extraDetails: ExtraDetails } | null {
  const lines = initialData.coupon_lines
  if (!Array.isArray(lines) || lines.length === 0) return null

  const first = lines[0] as Record<string, unknown>
  const amount = parseCouponAmount(first.nominal_amount ?? first.discount)
  if (amount <= 0) return null

  const prev = initialData.extraDetails || {}
  return {
    extraDetails: {
      ...prev,
      retencion_fuente_activa: true,
      retencion_fuente: String(amount)
    }
  }
}
